/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IImageProps, Image } from '@fluentui/react/lib/Image';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { routeTable } from 'src/core/utils/routeTable';

export type EmptyStateProps = {
    imageSrc?: string;
    message?: React.ReactNode;
    trainingPageLink?: string;
    imageStyles?: IImageProps['styles'];
    addBackground?: boolean;
};

const messages = defineMessages({
    emptyStateMsg: {
        id: 'core.emptyState.emptyStateMsg',
        defaultMessage: "You don't have any models yet. Create a model with your labeled data in"
    },
    trainModel: {
        id: 'core.emptyState.trainModel',
        defaultMessage: 'Training jobs'
    },
    emptyStateImageAlt: {
        id: 'core.emptyState.emptyStateImageAlt',
        defaultMessage: 'Empty state icon'
    }
});

const theme = getScopedTheme('EmptyState');
const textStyles = classNamesFunction<ITextProps, ITextStyles>()({ root: { padding: '16px' } });

export const EmptyStateView = observer((props: EmptyStateProps) => {
    const {
        imageStyles,
        trainingPageLink,
        addBackground = true,
        message: emptyStateMsg,
        imageSrc = getAssetsUrl(`assets/images/customText/newEmptyState.svg`)
    } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const { projectId } = useParams<{ projectId: string }>();

    const routeToTraining = () => history.push(trainingPageLink ?? routeTable.customText.trainingPage(projectId));

    return (
        <Stack
            verticalFill
            verticalAlign="center"
            horizontalAlign="center"
            tokens={{ childrenGap: 25 }}
            style={{ backgroundColor: addBackground && theme.backgroundColor }}>
            <Image styles={imageStyles} src={imageSrc} alt={localize(messages.emptyStateImageAlt)} />

            <LuisText styles={textStyles}>
                {emptyStateMsg ?? (
                    <>
                        {localize(messages.emptyStateMsg)}
                        &nbsp;
                        <RBACTooltip
                            hidden={canWrite}
                            displayedUserRole={displayedUserRole}
                            data-automation-id="trainModelLinkLanguageToolTip"
                            componentSurroundedByToolTip={
                                <Link
                                    disabled={!canWrite}
                                    onClick={routeToTraining}
                                    styles={{ root: { color: theme.emptyStateLinkColor } }}>
                                    {localize(messages.trainModel)}
                                </Link>
                            }
                        />
                    </>
                )}
            </LuisText>
        </Stack>
    );
});
